import React from 'react';
import PropTypes from 'prop-types';
import { startCase } from 'lodash';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';

import ImageDetailsCell from 'atoms/TableCell/ImageDetailsCell';
import Pagination from 'atoms/Pagination';
import ConfirmationModal from 'components/ConfirmationModal';
import { getRoleNameFromId } from 'utils/fetchRole';
import { filterByLetter } from 'utils/helpers';
import { compareWords } from 'utils/columnCompare';

import './index.css';

class ManageAccountStatus extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      modal: true,
      userFilter: '',
      page: 0,
      usersToShow: 5,
      usersToShowDropdown: false,
      selectedUser: null,
      showConfirmModal: false,
      confirmAction: null, // 'deactivate' or 'reactivate'
    };
  }

  componentDidMount() {
    const { fetchUsers } = this.props;
    fetchUsers();
  }

  componentDidUpdate(prevProps) {
    const { actionLoading, actionError, fetchUsers } = this.props;
    // After a successful deactivate/reactivate, refresh the user list
    if (prevProps.actionLoading && !actionLoading && !actionError) {
      fetchUsers();
      this.setState({ showConfirmModal: false, selectedUser: null, confirmAction: null });
    }
  }

  toggle = () => {
    const { toggle, toggleSettings } = this.props;
    toggle();
    toggleSettings();
  };

  handleChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  };

  toggleNumUsersDropdown = () => {
    this.setState({ usersToShowDropdown: !this.state.usersToShowDropdown });
  };

  handleDropdownClick = (e) => {
    this.setState({
      usersToShow: parseInt(e.currentTarget.textContent),
      page: 0,
    });
  };

  selectUser = (selectedUser) => {
    this.setState({ selectedUser });
  };

  backToList = () => {
    this.setState({ selectedUser: null });
  };

  handleDeactivateClick = () => {
    this.setState({ showConfirmModal: true, confirmAction: 'deactivate' });
  };

  handleReactivateClick = () => {
    this.setState({ showConfirmModal: true, confirmAction: 'reactivate' });
  };

  handleConfirm = () => {
    const { deactivateUser, reactivateUser } = this.props;
    const { selectedUser, confirmAction } = this.state;
    if (!selectedUser) return;
    if (confirmAction === 'deactivate') {
      deactivateUser(selectedUser.id);
    } else if (confirmAction === 'reactivate') {
      reactivateUser(selectedUser.id);
    }
  };

  handleCancelConfirm = () => {
    this.setState({ showConfirmModal: false, confirmAction: null });
  };

  createDropdown = () => {
    const dropdownOptions = [5, 10, 20];
    const { usersToShowDropdown, usersToShow } = this.state;

    return (
      <Dropdown isOpen={usersToShowDropdown} toggle={this.toggleNumUsersDropdown}>
        <DropdownToggle nav caret>
          <span className="text-dark font-13">{usersToShow}</span>
        </DropdownToggle>
        <DropdownMenu flip={false}>
          {dropdownOptions.map((option) => (
            <DropdownItem onClick={this.handleDropdownClick} key={option}>
              {option}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    );
  };

  render() {
    const {
      userFilter,
      page,
      usersToShow,
      selectedUser,
      showConfirmModal,
      confirmAction,
    } = this.state;
    const { users, user, loading, error, actionLoading, actionError } = this.props;

    // Filter out the current admin from the list
    const usersExcludingSelf = (users || []).filter((u) => u.id !== user.id);
    const filteredUsers = filterByLetter(usersExcludingSelf, 'name', userFilter).sort(
      (a, b) => compareWords(a.name, b.name, 'asc'),
    );
    const start = page * usersToShow;
    const end = Math.min(start + usersToShow, filteredUsers.length);
    const usersOnScreen = filteredUsers.slice(start, end);

    return (
      <div>
        <Modal
          className="modal-dialog-centered"
          isOpen={this.state.modal}
          toggle={this.toggle}
        >
          <ModalHeader toggle={this.toggle} className="border-remove" />
          <ModalBody>
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <h3 className="m-0 heading-update-pass">Manage Account Status</h3>
                  {actionError && (
                    <div className="d-flex justify-content-center error-font mb-2 mt-3">
                      {actionError}
                    </div>
                  )}
                  {error && (
                    <div className="d-flex justify-content-center error-font mb-2 mt-3">
                      {error}
                    </div>
                  )}
                  <div className="mt-3">
                    {!selectedUser ? (
                      <div>
                        <div className="row px-3">
                          <span className="form-font pt-3 font-13">Users to show</span>
                          {this.createDropdown()}
                        </div>
                        <div style={{ position: 'relative' }}>
                          <input
                            onChange={this.handleChange}
                            name="userFilter"
                            className="form-control"
                            placeholder="Search by name..."
                            value={userFilter}
                          />
                          {userFilter.length > 0 && (
                            <span
                              onClick={() => this.setState({ userFilter: '', page: 0 })}
                              className="hand"
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                color: 'red',
                              }}
                            >
                              X
                            </span>
                          )}
                        </div>
                        {loading ? (
                          <div className="text-center mt-4 mb-3">Loading...</div>
                        ) : (
                          <div>
                            {usersOnScreen.map((u) => (
                              <div
                                className="my-3 mx-0 hand"
                                onClick={() => this.selectUser(u)}
                                key={u.id}
                              >
                                <div className="d-flex align-items-center">
                                  <div className="flex-grow-1">
                                    <ImageDetailsCell
                                      heading={u.name}
                                      primarySubHeading={startCase(
                                        getRoleNameFromId(u.role),
                                      )}
                                      secondarySubHeading={u.email}
                                      classes={{
                                        primarySubHeading: 'font-italic',
                                      }}
                                    />
                                  </div>
                                  <div className="ml-2">
                                    <span
                                      className={`badge ${
                                        u.isActive === false
                                          ? 'badge-secondary'
                                          : 'badge-success'
                                      }`}
                                    >
                                      {u.isActive === false ? 'Inactive' : 'Active'}
                                    </span>
                                  </div>
                                </div>
                                <hr />
                              </div>
                            ))}
                            {usersOnScreen.length === 0 && !loading && (
                              <div className="text-center mt-4 mb-3 font-13 text-muted">
                                No users found
                              </div>
                            )}
                            <Pagination
                              setPage={(p) => this.setState({ page: p })}
                              page={page}
                              data={filteredUsers}
                              start={start}
                              end={end}
                              dataPerPage={usersToShow}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="d-flex flex-column">
                        <span
                          className="font-11 link-color hand"
                          onClick={this.backToList}
                        >
                          <strong>Back</strong>
                        </span>
                        <div className="mt-3 mb-2">
                          <h5 className="mb-1">{selectedUser.name}</h5>
                          <p className="text-muted font-13 mb-1">{selectedUser.email}</p>
                          <span className="badge badge-dark font-10">
                            {startCase(getRoleNameFromId(selectedUser.role))}
                          </span>
                          <span
                            className={`badge ml-2 font-10 ${
                              selectedUser.isActive === false
                                ? 'badge-secondary'
                                : 'badge-success'
                            }`}
                          >
                            {selectedUser.isActive === false ? 'Inactive' : 'Active'}
                          </span>
                        </div>
                        <div className="mt-4 mb-2">
                          {selectedUser.isActive === false ? (
                            <button
                              type="button"
                              className="btn btn-primary btn-lg btn-block btn-color text-uppercase font-13"
                              onClick={this.handleReactivateClick}
                              disabled={actionLoading}
                            >
                              {actionLoading ? 'PLEASE WAIT...' : 'REACTIVATE ACCOUNT'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-danger btn-lg btn-block text-uppercase font-13"
                              onClick={this.handleDeactivateClick}
                              disabled={actionLoading}
                            >
                              {actionLoading ? 'PLEASE WAIT...' : 'DEACTIVATE ACCOUNT'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>

        {showConfirmModal && (
          <ConfirmationModal
            isOpen={showConfirmModal}
            title={
              confirmAction === 'deactivate'
                ? 'Deactivate Account'
                : 'Reactivate Account'
            }
            message={
              confirmAction === 'deactivate'
                ? `Are you sure you want to deactivate ${selectedUser?.name}'s account? They will no longer be able to log in.`
                : `Are you sure you want to reactivate ${selectedUser?.name}'s account? They will be able to log in again.`
            }
            confirmText={confirmAction === 'deactivate' ? 'Deactivate' : 'Reactivate'}
            cancelText="Cancel"
            onConfirm={this.handleConfirm}
            onCancel={this.handleCancelConfirm}
            loading={actionLoading}
            variant={confirmAction === 'deactivate' ? 'danger' : 'default'}
          />
        )}
      </div>
    );
  }
}

ManageAccountStatus.propTypes = {
  user: PropTypes.object.isRequired,
  users: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  actionLoading: PropTypes.bool,
  actionError: PropTypes.string,
  fetchUsers: PropTypes.func.isRequired,
  deactivateUser: PropTypes.func.isRequired,
  reactivateUser: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  toggleSettings: PropTypes.func.isRequired,
};

export default ManageAccountStatus;
